'use client';

import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageGalleryProps {
    images: {
        src: string;
        alt: string;
        width?: number;
        height?: number;
    }[];
    gridClassName?: string;
    imageClassName?: string;
    aspectRatio?: 'square' | 'video' | 'auto';
}

export default function ImageGallery({
                                         images,
                                         gridClassName = "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4",
                                         imageClassName = "rounded-lg hover:opacity-90 transition-opacity cursor-pointer",
                                         aspectRatio = 'square'
                                     }: ImageGalleryProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const openModal = (index: number) => {
        setCurrentIndex(index);
        setIsOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = useCallback(() => {
        setIsOpen(false);
        document.body.style.overflow = 'auto';
    }, []);

    const goToPrevious = useCallback((e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
    }, [images.length]);

    const goToNext = useCallback((e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
    }, [images.length]);

    // Gestion des raccourcis clavier
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'Escape':
                    closeModal();
                    break;
                case 'ArrowLeft':
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    goToNext();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, closeModal, goToPrevious, goToNext]);

    // Déterminer la classe d'aspect ratio
    const getAspectRatioClass = () => {
        switch (aspectRatio) {
            case 'square':
                return 'aspect-square';
            case 'video':
                return 'aspect-video';
            default:
                return '';
        }
    };

    return (
        <>
            {/* Grille d'images */}
            <div className={gridClassName}>
                {images.map((image, index) => (
                    <div
                        key={index}
                        className={cn("relative overflow-hidden", getAspectRatioClass())}
                        onClick={() => openModal(index)}
                    >
                        <img
                            src={image.src}
                            alt={image.alt}
                            className={cn("object-cover", imageClassName)}
                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        />
                    </div>
                ))}
            </div>

            {/* Modal de carrousel */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center"
                    onClick={closeModal}
                >
                    <div
                        className="relative max-w-4xl w-full h-full flex items-center justify-center p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 z-10"
                            onClick={closeModal}
                            aria-label="Fermer"
                        >
                            <X size={24} />
                        </button>

                        <button
                            className="absolute left-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 z-10"
                            onClick={goToPrevious}
                            aria-label="Image précédente"
                        >
                            <ChevronLeft size={24} />
                        </button>

                        <div className="relative w-full max-h-[80vh] flex items-center justify-center">
                            <img
                                src={images[currentIndex].src}
                                alt={images[currentIndex].alt}
                                width={images[currentIndex].width || 1200}
                                height={images[currentIndex].height || 800}
                                className="max-h-[80vh] w-auto object-contain"
                            />
                            <div className="absolute bottom-4 left-0 right-0 text-center text-white bg-black bg-opacity-50 py-2 px-4">
                                <p className="text-sm md:text-base">{currentIndex + 1} / {images.length}</p>
                                <p className="text-sm md:text-base truncate">{images[currentIndex].alt}</p>
                            </div>
                        </div>

                        <button
                            className="absolute right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 z-10"
                            onClick={goToNext}
                            aria-label="Image suivante"
                        >
                            <ChevronRight size={24} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
